/**
 * OtpInput component — shared types, Zod prop schema, and pure helpers.
 *
 * A fixed-length segmented code input: `length` cells over a single hidden
 * text field that owns the value, focus, paste, and OS autofill. The visible
 * cells are a presentation overlay that highlights the active position.
 *
 * Source of truth: Pangea Galapagos UI Design System — Beta
 * https://www.figma.com/design/Orc6uUodQaoXep59CGaUNw
 */

import { z } from 'zod';
import { colors } from '../../elements/colors';
import { spacing } from '../../elements/spacing';
import { radius } from '../../elements/radius';
import { typography } from '../../elements/typography';

/** Catalog-facing schema (plain ZodObject so `formatZodType` can introspect it). */
export const otpInputPropsSchema = z.object({
  length: z.number().int().min(1).default(6),
  value: z.string().default(''),
  disabled: z.boolean().default(false),
  error: z.boolean().default(false),
});

export type OtpInputProps = z.infer<typeof otpInputPropsSchema>;

/** Strip non-digits and cap to `max` cells — the single normalisation point. */
export function sanitizeOtpDigits(raw: string, max: number): string {
  return raw.replace(/\D/g, '').slice(0, Math.max(0, max));
}

/** True once every cell holds a digit. */
export function isOtpComplete(value: string, length: number): boolean {
  return sanitizeOtpDigits(value, length).length === length && length > 0;
}

/** The cell that should read as active — the first empty cell, clamped to the last. */
export function activeOtpIndex(value: string, length: number): number {
  return Math.min(sanitizeOtpDigits(value, length).length, length - 1);
}

// Cells visually match the Textfield: 70px tall, 16px corner (Figma `spacing/m`
// → `radius.xl`, the same mapping TextInput uses), 1px divider border.
export const OTP_CELL_HEIGHT = 70;
export const OTP_CELL_RADIUS = radius.xl;
export const OTP_CELL_BORDER_WIDTH = 1;
export const OTP_CELL_GAP = spacing.xs;

export const OTP_CELL_BACKGROUND = colors.background.container;
export const OTP_CELL_BORDER_COLOR = colors.stroke.divider;
export const OTP_CELL_BORDER_COLOR_FOCUSED = colors.stroke.primary;
export const OTP_CELL_BORDER_COLOR_ERROR = colors.text.alert;
export const OTP_CELL_TEXT_COLOR = colors.text.primary;
export const OTP_CELL_TYPOGRAPHY = typography.h2Bold;

/** Border colour for a cell given the active/error state. */
export function otpCellBorderColor(active: boolean, error: boolean): string {
  if (error) return OTP_CELL_BORDER_COLOR_ERROR;
  if (active) return OTP_CELL_BORDER_COLOR_FOCUSED;
  return OTP_CELL_BORDER_COLOR;
}

/** Compose the per-cell accessibility name, including the entered digit when present. */
export function resolveOtpCellAccessibilityLabel(
  labelForDigit: ((digit: number) => string) | undefined,
  cellIndex: number,
  value: string,
): string | undefined {
  if (!labelForDigit) return undefined;
  const position = cellIndex + 1;
  const base = labelForDigit(position);
  const char = value[cellIndex];
  if (!char) return base;
  return `${base}, ${char}`;
}
